import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useStore } from '../lib/store'

/**
 * StationApprovePage — opened on the clinician's PHONE (already authenticated).
 * Confirms a shared-computer login request identified by the short user code.
 * If not logged in on this device, bounce to /login and return here.
 */
export function StationApprovePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const isAuthed = useStore((s: any) => s.isAuthenticated)
  const [code, setCode] = useState((params.get('code') || '').toUpperCase())
  const [state, setState] = useState<'idle' | 'working' | 'approved' | 'denied' | 'error'>('idle')
  const [msg, setMsg] = useState('')

  useEffect(() => {
    if (!isAuthed) {
      // Send them to login on THEIR phone, then come back to this approve URL.
      const back = `/station-approve${code ? `?code=${encodeURIComponent(code)}` : ''}`
      sessionStorage.setItem('scribey_post_login', back)
      navigate('/login')
    }
  }, [isAuthed]) // eslint-disable-line react-hooks/exhaustive-deps

  async function approve() {
    setState('working'); setMsg('')
    try {
      await api.deviceApprove(code.trim().toUpperCase())
      setState('approved')
    } catch (e: any) {
      setMsg(e?.message || 'Could not approve'); setState('error')
    }
  }
  async function deny() {
    setState('working')
    try { await api.deviceDeny(code.trim().toUpperCase()); setState('denied') }
    catch (e: any) { setMsg(e?.message || 'Could not decline'); setState('error') }
  }

  if (!isAuthed) return null

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: 24 }}>
      <div style={{ width: 400, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,.08)', padding: 32 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, color: '#0f172a' }}>Approve computer sign-in?</h1>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
          A computer is requesting to sign in as you. Only approve if you just started a login on a trusted computer and the code matches.
        </p>

        {state === 'approved' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40 }}>✓</div>
            <p style={{ color: '#16a34a', fontWeight: 600 }}>Approved</p>
            <p style={{ color: '#64748b', fontSize: 13 }}>The computer will sign in automatically. You can close this page.</p>
          </div>
        ) : state === 'denied' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#dc2626', fontWeight: 600 }}>Declined</p>
            <p style={{ color: '#64748b', fontSize: 13 }}>No session was created. You can close this page.</p>
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Code shown on the computer</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              style={{ width: '100%', fontSize: 26, fontWeight: 700, letterSpacing: 3, fontFamily: 'monospace', textAlign: 'center', padding: '12px', margin: '8px 0 20px', border: '2px solid #e2e8f0', borderRadius: 10, color: '#0f172a' }}
            />
            {msg && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={deny} disabled={state === 'working'} style={{ flex: 1, background: '#fff', color: '#dc2626', border: '2px solid #fecaca', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Decline</button>
              <button onClick={approve} disabled={state === 'working' || !code.trim()} style={{ flex: 2, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: (state === 'working' || !code.trim()) ? .6 : 1 }}>
                {state === 'working' ? 'Approving…' : 'Approve sign-in'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
